import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useApolloClient } from '@apollo/client';
import { 
  GET_UNASSIGNED_EXERCISES, 
  GET_TRAINING_EXERCISES, 
  UPDATE_EXERCISE 
} from '../graphql/exerciseOperations';
import { toast } from 'react-toastify';
import { TrashIcon, XMarkIcon, ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';

const AddExercisesToTrainingModal = ({ trainingId, onClose }) => {
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [orderedTrainingExercises, setOrderedTrainingExercises] = useState([]);
  const client = useApolloClient();

  // Fetch unassigned exercises
  const { data: unassignedData, loading: unassignedLoading } = useQuery(GET_UNASSIGNED_EXERCISES);

  // Fetch current training exercises
  const { data: trainingExercisesData, loading: trainingExercisesLoading } = useQuery(
    GET_TRAINING_EXERCISES, 
    { 
      variables: { trainingId },
      onCompleted: (data) => {
        const exercises = data?.getAllExercises?.exercises || [];
        setOrderedTrainingExercises(
          [...exercises].sort((a, b) => (a.trainingDay || Infinity) - (b.trainingDay || Infinity))
        );
      }
    }
  );

  // Mutation to update exercise
  const [updateExercise] = useMutation(UPDATE_EXERCISE, {
    refetchQueries: [
      { query: GET_UNASSIGNED_EXERCISES },
      { 
        query: GET_TRAINING_EXERCISES, 
        variables: { trainingId } 
      }
    ],
    onCompleted: (data) => {
      // Refresh the ordered exercises list after mutation
      if (trainingExercisesData?.getAllExercises?.exercises) {
        const exercises = [...trainingExercisesData.getAllExercises.exercises];
        setOrderedTrainingExercises(
          exercises.sort((a, b) => (a.trainingDay || Infinity) - (b.trainingDay || Infinity))
        );
      }
    }
  });

  const handleMoveExercise = async (index, direction) => {
    try {
      // Create a new ordered list
      const items = Array.from(orderedTrainingExercises);
      
      // Calculate new index based on direction
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      
      // Check if the new index is valid
      if (newIndex < 0 || newIndex >= items.length) {
        return; // Can't move outside bounds
      }
      
      // Swap the items
      [items[index], items[newIndex]] = [items[newIndex], items[index]];
      
      // Update training days based on new order
      const updatedItems = items.map((item, idx) => ({
        ...item,
        trainingDay: idx + 1
      }));
      
      // Update UI immediately
      setOrderedTrainingExercises(updatedItems);
      
      // Update in database
      const updatePromises = updatedItems.map(exercise => 
        updateExercise({
          variables: {
            updateExerciseId: exercise.id,
            input: {
              trainingDay: exercise.trainingDay
            }
          }
        })
      );
      
      await Promise.all(updatePromises);
      toast.success('Exercise reordered');
    } catch (error) {
      console.error('Error reordering exercise:', error);
      toast.error('Failed to reorder exercise');
      
      // Revert to original order on error
      if (trainingExercisesData?.getAllExercises?.exercises) {
        const exercises = trainingExercisesData.getAllExercises.exercises;
        setOrderedTrainingExercises(
          [...exercises].sort((a, b) => (a.trainingDay || Infinity) - (b.trainingDay || Infinity))
        );
      }
    }
  };

  const handleAddExercises = async () => {
    try {
      // Find the next available training day
      const existingTrainingDays = orderedTrainingExercises.map(ex => ex.trainingDay).filter(Boolean);
      let nextAvailableDay = 1;
      while (existingTrainingDays.includes(nextAvailableDay)) {
        nextAvailableDay++;
      }

      const updatePromises = selectedExercises.map((exerciseId) => {
        const dayToAssign = nextAvailableDay;
        // Increment nextAvailableDay for the next exercise
        while (existingTrainingDays.includes(nextAvailableDay)) {
          nextAvailableDay++;
        }
        nextAvailableDay++;

        return updateExercise({
          variables: {
            updateExerciseId: exerciseId,
            input: {
              trainingId,
              trainingDay: dayToAssign
            }
          }
        });
      });

      await Promise.all(updatePromises);
      
      // Manually refresh the data
      const { data } = await client.query({
        query: GET_TRAINING_EXERCISES,
        variables: { trainingId },
        fetchPolicy: 'network-only'
      });
      
      if (data?.getAllExercises?.exercises) {
        const exercises = data.getAllExercises.exercises;
        setOrderedTrainingExercises(
          [...exercises].sort((a, b) => (a.trainingDay || Infinity) - (b.trainingDay || Infinity))
        );
      }
      
      toast.success(`${selectedExercises.length} exercises added to training`);
      setSelectedExercises([]);
    } catch (error) {
      toast.error('Failed to add exercises to training');
      console.error(error);
    }
  };

  const handleRemoveExercise = async (exerciseId) => {
    try {
      // Remove the exercise from the training
      await updateExercise({
        variables: {
          updateExerciseId: exerciseId,
          input: {
            trainingId: null,
            trainingDay: null
          }
        }
      });

      // Immediately update local state by removing the exercise
      setOrderedTrainingExercises(prev => prev.filter(ex => ex.id !== exerciseId));

      // Rearrange remaining exercises
      const currentTrainingExercises = [...(trainingExercisesData?.getAllExercises?.exercises || [])]
        .filter(ex => ex.id !== exerciseId)
        .sort((a, b) => (a.trainingDay || Infinity) - (b.trainingDay || Infinity));

      // Update local state with new training days
      const updatedExercises = currentTrainingExercises.map((exercise, index) => ({
        ...exercise,
        trainingDay: index + 1
      }));
      setOrderedTrainingExercises(updatedExercises);

      // Update training days for remaining exercises
      const updatePromises = updatedExercises.map((exercise) => 
        updateExercise({
          variables: {
            updateExerciseId: exercise.id,
            input: {
              trainingDay: exercise.trainingDay
            }
          }
        })
      );

      await Promise.all(updatePromises);
      
      // Manually refresh the data
      const { data } = await client.query({
        query: GET_TRAINING_EXERCISES,
        variables: { trainingId },
        fetchPolicy: 'network-only'
      });
      
      if (data?.getAllExercises?.exercises) {
        const exercises = data.getAllExercises.exercises;
        setOrderedTrainingExercises(
          [...exercises].sort((a, b) => (a.trainingDay || Infinity) - (b.trainingDay || Infinity))
        );
      }
      
      toast.success('Exercise removed and training days rearranged');
    } catch (error) {
      toast.error('Failed to remove exercise from training');
      console.error(error);
    }
  };

  const unassignedExercises = unassignedData?.getAllExercises?.exercises || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-3/4 max-w-4xl relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
        <h2 className="text-2xl font-bold mb-4 dark:text-white">
          Add Exercises to Training
        </h2>

        <div className="grid grid-cols-2 gap-4">
          {/* Unassigned Exercises */}
          <div>
            <h3 className="text-lg font-semibold mb-2 dark:text-gray-200">
              Available Exercises
            </h3>
            <div className="max-h-96 overflow-y-auto border rounded">
              {unassignedLoading ? (
                <p className="p-4 text-center">Loading...</p>
              ) : (
                unassignedExercises.map((exercise) => (
                  <div
                    key={exercise.id}
                    className={`
                      p-2 flex items-center cursor-pointer 
                      hover:bg-blue-50 hover:text-blue-800
                      ${selectedExercises.includes(exercise.id)
                        ? 'bg-blue-100 text-blue-900'
                        : 'hover:bg-gray-100'}
                      transition-colors duration-200
                    `}
                    onClick={() => {
                      setSelectedExercises((prev) =>
                        prev.includes(exercise.id)
                          ? prev.filter((id) => id !== exercise.id)
                          : [...prev, exercise.id]
                      );
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedExercises.includes(exercise.id)}
                      className="mr-2"
                      readOnly
                    />
                    <img
                      src={exercise.imageUrl || '/default-exercise.png'}
                      alt={exercise.name}
                      className="w-12 h-12 object-cover mr-2 rounded"
                    />
                    <span className="flex-grow">{exercise.name}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Current Training Exercises */}
          <div>
            <h3 className="text-lg font-semibold mb-2 dark:text-gray-200">
              Current Training Exercises
            </h3>
            <div className="max-h-96 overflow-auto border rounded">
              {trainingExercisesLoading ? (
                <p className="p-4 text-center">Loading...</p>
              ) : orderedTrainingExercises.length === 0 ? (
                <p className="p-4 text-center text-gray-500">No exercises added yet</p>
              ) : (
                <div className="min-h-[100px]">
                  {orderedTrainingExercises.map((exercise, index) => (
                    <div
                      key={exercise.id}
                      className="p-2 flex items-center border-b border-gray-200 mb-1 group hover:bg-blue-50 hover:text-blue-800 transition-colors duration-200"
                    >
                      <img
                        src={exercise.imageUrl || '/default-exercise.png'}
                        alt={exercise.name}
                        className="w-12 h-12 object-cover mr-2 rounded"
                      />
                      <div className="flex-grow flex items-center justify-between">
                        <span className="flex-grow">{exercise.name}</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500 mr-2">
                            Day {exercise.trainingDay}
                          </span>
                          <div className="flex space-x-1">
                            <button
                              onClick={() => handleMoveExercise(index, 'up')}
                              disabled={index === 0}
                              className={`
                                p-1 rounded-full 
                                ${index === 0
                                  ? 'text-gray-300 cursor-not-allowed'
                                  : 'text-blue-500 hover:text-blue-700 hover:bg-blue-100'}
                              `}
                              title="Move up"
                            >
                              <ArrowUpIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleMoveExercise(index, 'down')}
                              disabled={index === orderedTrainingExercises.length - 1}
                              className={`
                                p-1 rounded-full 
                                ${index === orderedTrainingExercises.length - 1
                                  ? 'text-gray-300 cursor-not-allowed'
                                  : 'text-blue-500 hover:text-blue-700 hover:bg-blue-100'}
                              `}
                              title="Move down"
                            >
                              <ArrowDownIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleRemoveExercise(exercise.id)}
                              className="
                                text-red-500 hover:text-red-700 
                                hover:bg-red-100 p-1 rounded-full 
                              "
                              title="Remove from training"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleAddExercises}
            disabled={selectedExercises.length === 0}
            className={`
              px-4 py-2 rounded 
              ${selectedExercises.length > 0
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'}
            `}
          >
            Add {selectedExercises.length} Exercise{selectedExercises.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}; 


export default AddExercisesToTrainingModal;
